/**
 * Network Registry Configuration
 * Central configuration for all supported blockchain networks
 * 
 * This file provides a single source of truth for:
 * - Network connection details
 * - Smart contract addresses per network
 * - Token configurations per network
 * - Block explorer URLs
 */

export const NETWORK_REGISTRY = {
  ethereum: {
    id: 'ethereum',
    name: 'Ethereum Mainnet',
    chainId: 1,
    chainIdHex: '0x1',
    rpcUrl: process.env.REACT_APP_ETHEREUM_RPC_URL || 'https://mainnet.infura.io/v3/your-infura-id',
    blockExplorer: 'https://etherscan.io',
    nativeCurrency: {
      name: 'Ethereum',
      symbol: 'ETH',
      decimals: 18
    },
    contracts: {
      batchPayout: process.env.REACT_APP_BATCH_PAYOUT_CONTRACT || '0xFA49b46e3c1677C4973269De91c1Cc6CC0e0a74a',
      owner: process.env.REACT_APP_BATCH_PAYOUT_OWNER || '0x786C4D80FCCA08877ffc0D7538e21194212a6860'
    },
    tokens: {
      USDC: {
        address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
        decimals: 6,
        symbol: 'USDC',
        name: 'USD Coin'
      },
      USDT: {
        address: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
        decimals: 6,
        symbol: 'USDT',
        name: 'Tether USD'
      }
    },
    isMainnet: true,
    isTestnet: false
  },
  
  sepolia: {
    id: 'sepolia',
    name: 'Sepolia Testnet',
    chainId: 11155111,
    chainIdHex: '0xaa36a7',
    rpcUrl: process.env.REACT_APP_SEPOLIA_RPC_URL || 'https://sepolia.infura.io/v3/your-infura-id',
    blockExplorer: 'https://sepolia.etherscan.io',
    nativeCurrency: {
      name: 'Ethereum',
      symbol: 'ETH',
      decimals: 18
    },
    contracts: {
      batchPayout: process.env.REACT_APP_SEPOLIA_BATCH_PAYOUT_CONTRACT || '0x7C680C0BfB0154B96ee09B6309f63d81A129fB4a',
      owner: process.env.REACT_APP_SEPOLIA_BATCH_PAYOUT_OWNER || '0x786C4D80FCCA08877ffc0D7538e21194212a6860'
    },
    tokens: {
      USDC: {
        address: process.env.REACT_APP_SEPOLIA_USDC_CONTRACT || '0x1234567890123456789012345678901234567890',
        decimals: 6,
        symbol: 'USDC',
        name: 'USD Coin (Testnet)'
      },
      USDT: {
        address: process.env.REACT_APP_SEPOLIA_USDT_CONTRACT || '0x1234567890123456789012345678901234567890',
        decimals: 6,
        symbol: 'USDT',
        name: 'Tether USD (Testnet)'
      }
    },
    isMainnet: false,
    isTestnet: true
  },
  
  polygon: {
    id: 'polygon',
    name: 'Polygon',
    chainId: 137,
    chainIdHex: '0x89',
    rpcUrl: process.env.REACT_APP_POLYGON_RPC_URL || 'https://polygon-rpc.com',
    blockExplorer: 'https://polygonscan.com',
    nativeCurrency: {
      name: 'MATIC',
      symbol: 'MATIC',
      decimals: 18
    },
    contracts: {
      batchPayout: process.env.REACT_APP_POLYGON_BATCH_PAYOUT_CONTRACT,
      owner: process.env.REACT_APP_POLYGON_BATCH_PAYOUT_OWNER
    },
    tokens: {
      USDC: {
        address: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174',
        decimals: 6,
        symbol: 'USDC',
        name: 'USD Coin (PoS)'
      },
      USDT: {
        address: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F',
        decimals: 6,
        symbol: 'USDT',
        name: '(PoS) Tether USD'
      }
    },
    isMainnet: true,
    isTestnet: false
  }
};

/**
 * Network resolution configuration
 * Determines which network to use based on environment
 */
export const NETWORK_RESOLUTION_CONFIG = {
  // Environment-based network selection
  production: 'ethereum',
  staging: 'sepolia',
  development: 'sepolia',
  test: 'sepolia',
  
  // Override flags
  forceMainnet: process.env.REACT_APP_FORCE_MAINNET === 'true',
  forceNetwork: process.env.REACT_APP_FORCE_NETWORK,
  defaultNetwork: process.env.REACT_APP_DEFAULT_NETWORK || 'sepolia'
};

/**
 * Get network configuration by ID
 */
export function getNetworkById(networkId) {
  return NETWORK_REGISTRY[networkId] || null;
}

/**
 * Get network configuration by chain ID
 */
export function getNetworkByChainId(chainId) {
  const numericChainId = typeof chainId === 'string' ? parseInt(chainId, 16) : chainId;
  return Object.values(NETWORK_REGISTRY).find(n => n.chainId === numericChainId) || null;
}

/**
 * Get all available networks
 */
export function getAllNetworks() {
  return Object.values(NETWORK_REGISTRY);
}

/**
 * Get mainnet networks only
 */
export function getMainnetNetworks() {
  return Object.values(NETWORK_REGISTRY).filter(n => n.isMainnet);
}

/**
 * Get testnet networks only  
 */
export function getTestnetNetworks() {
  return Object.values(NETWORK_REGISTRY).filter(n => n.isTestnet);
}

/**
 * Resolve default network based on environment
 */
export function resolveDefaultNetwork() {
  const config = NETWORK_RESOLUTION_CONFIG;
  
  // Check for explicit network override
  if (config.forceNetwork && NETWORK_REGISTRY[config.forceNetwork]) {
    return NETWORK_REGISTRY[config.forceNetwork];
  }
  
  // Check for mainnet override
  if (config.forceMainnet) {
    return NETWORK_REGISTRY.ethereum;
  }
  
  // Environment-based resolution
  const env = process.env.NODE_ENV || 'development';
  const networkId = config[env] || config.defaultNetwork;
  
  return NETWORK_REGISTRY[networkId] || NETWORK_REGISTRY.ethereum;
}

/**
 * Get default token for a network
 */
export function getDefaultToken(networkId) {
  const network = getNetworkById(networkId);
  if (!network || !network.tokens) return null;
  
  // Prefer USDC, fallback to first available token
  return network.tokens.USDC || Object.values(network.tokens)[0] || null;
}

/**
 * Validate network configuration
 */
export function validateNetwork(network) {
  const required = ['id', 'name', 'chainId', 'chainIdHex', 'rpcUrl', 'blockExplorer'];
  
  for (const field of required) {
    if (!network[field]) {
      throw new Error(`Network configuration missing required field: ${field}`);
    }
  }
  
  // Validate chain ID consistency
  const hexChainId = parseInt(network.chainIdHex, 16);
  if (hexChainId !== network.chainId) {
    throw new Error(`Chain ID mismatch: ${network.chainId} !== ${hexChainId}`);
  }
  
  return true;
}

/**
 * Export default configuration for backward compatibility
 */
export default {
  networks: NETWORK_REGISTRY,
  resolution: NETWORK_RESOLUTION_CONFIG,
  getNetworkById,
  getNetworkByChainId,
  getAllNetworks,
  getMainnetNetworks,
  getTestnetNetworks,
  resolveDefaultNetwork,
  getDefaultToken,
  validateNetwork
};